import { useDropzone } from 'react-dropzone';
import { Box, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FiUploadCloud } from 'react-icons/fi';
import PropTypes from 'prop-types';

/**
 * The dropzone as a single dashed target.
 *
 * It used to be a solid `Paper` with an italic hint on the left and a filled button pushed
 * to the right, which read as two controls sitting in a card. Dropping a file onto a button
 * is not an idea the shape suggested. One dashed rectangle says "put something here".
 */
export default function Upload(props) {
  const {
    sx,
    accept,
    maxFiles,
    disabled,
    onDrop,
    hint,
    ...otherProps
  } = props;
  const { t } = useTranslation();
  const {
    getRootProps,
    getInputProps,
    isDragActive,
  } = useDropzone({
    accept,
    maxFiles,
    onDrop,
  });

  return (
    <Paper
      {...getRootProps()}
      {...otherProps}
      data-e2e-id="upload-dropzone"
      sx={{
        // `variant="outlined"` supplies the border colour and the theme radius; only the
        // dash and the drag feedback are ours.
        borderStyle: 'dashed',
        borderWidth: 1.5,
        ...(isDragActive && {
          borderColor: 'primary.main',
          backgroundColor: 'quaternary.main',
        }),
        p: 2.75,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.25,
        textAlign: 'center',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        '&:hover': disabled ? {} : {
          borderColor: 'primary.main',
          backgroundColor: 'quaternary.main',
        },
        ...sx,
      }}
      variant="outlined"
    >
      <input
        {...getInputProps()}
        data-e2e-id="upload-input"
        disabled={disabled}
      />
      <Box aria-hidden color="text.secondary" sx={{ display: 'flex' }}>
        <FiUploadCloud size={18} />
      </Box>
      <Typography color="text.secondary" data-e2e-id="upload-button">
        {isDragActive
          ? t('components.molecules.form.Upload.drop')
          : t('components.molecules.form.Upload.placeholder')}
        {!isDragActive && hint && ` — ${hint}`}
      </Typography>
    </Paper>
  );
}

Upload.propTypes = {
  sx: PropTypes.object,
  accept: PropTypes.object,
  maxFiles: PropTypes.number,
  disabled: PropTypes.bool,
  onDrop: PropTypes.func.isRequired,
  hint: PropTypes.string,
};
